<script>
import axios from "axios";
import { formatDateUTC, calcAgeFromUTC } from "@/utils/date";
import { ALLOGGIATI_STATI } from "@/utils/alloggiatiStati.js";

export default {
  name: "ListCheckinGroup",
  props: {
    associates:    { type: Array,    required: true },
    edad:          { type: Function, required: true },
    property:      { type: Object,   required: true },
    isVisibleData: { type: Boolean,  required: true },
    isItaly:       { type: Boolean,  default: false },
    validateField: { type: Function, required: true },
    // Autoridad de registro: { key, label, flag }. Por defecto SES (compat).
    authority:     { type: Object,   default: () => ({ key: "ses_hospedaje", label: "SES Hospedaje", flag: "🇪🇸" }) },
  },
  data() {
    return {
      openIndex:    null,
      previewOpen:  false,
      previewUrl:   null,
      previewTitle: "",
      bajandoPlanilla: false,
    };
  },
  computed: {
    /* La planilla es de la RESERVA, no de un check-in suelto, así que el id sale de
       cualquiera de ellos: todos los de este grupo son de la misma. */
    reservaDeLaPlanilla() {
      const conReserva = (this.associates || []).find((a) => a && a.reserva_id);
      return conReserva ? conReserva.reserva_id : null;
    },
  },
  methods: {
    formatDateUTC,
    calcAgeFromUTC,

    /**
     * Abre la planilla del grupo: un solo documento con el titular arriba y sus
     * acompañantes debajo, que es lo que piden las autoridades.
     *
     * Se pide al servidor en vez de enlazar un fichero fijo porque el servidor la
     * REHACE: entre el último check-in y este clic el anfitrión ha podido corregir un
     * documento, y entregar una planilla vieja es peor que no tenerla.
     */
    async abrirPlanilla() {
      if (this.bajandoPlanilla) return;

      const reservaId = this.reservaDeLaPlanilla;
      if (!reservaId) {
        toastr.info(this.$t("checkinList.group.registrationFormEmpty"));
        return;
      }

      /* La pestaña se abre ANTES de la llamada y se rellena después: si se abriera al
         recibir la respuesta, Safari y Chrome la bloquean por no venir de un clic. */
      const pestana = window.open("", "_blank");
      this.bajandoPlanilla = true;

      try {
        const { data } = await axios.get(`/reservations/${reservaId}/registration-form`);

        if (data && data.ok && data.url) {
          if (pestana) pestana.location = data.url;
          else window.open(data.url, "_blank");
        } else {
          if (pestana) pestana.close();
          toastr.info(this.$t("checkinList.group.registrationFormEmpty"));
        }
      } catch (e) {
        if (pestana) pestana.close();
        const sinCheckins = e?.response?.status === 404;
        toastr[sinCheckins ? "info" : "error"](
          this.$t(sinCheckins ? "checkinList.group.registrationFormEmpty" : "checkinList.group.registrationFormError")
        );
      } finally {
        this.bajandoPlanilla = false;
      }
    },

    toggleDetails(index) {
      this.openIndex = this.openIndex === index ? null : index;
    },

    getMigrationColor(state) {
      if (state == 2) return "#16a34a";
      if (state == 3) return "#ef4444";
      return "#9ca3af";
    },

    openEditDrawer(item) {
      this.$emit("edit-checkin", { item });
    },
    emitRemove(item) {
      this.$emit("remove-checkin", item?.id || item);
    },

    openPreview(url, title) {
      if (!url) return;
      this.previewUrl   = url;
      this.previewTitle = title || this.$t("checkinList.group.previewFallbackTitle");
      this.previewOpen  = true;
    },
    closePreview() {
      this.previewOpen  = false;
      this.previewUrl   = null;
      this.previewTitle = "";
    },

    docTypeLabel(type) {
      const t = (type || "").toUpperCase().trim();
      const map = {
        P: "passport", PASS: "passport", PPT: "passport",
        PASOR: "passportOrdinary", PASSE: "passportService",
        PASDI: "passportDiplomatic",
        I: "idCard", ID: "idCard",
        IM: "nie", IDEN: "idCard", IDENT: "idCard",
        IDELE: "idCardElectronic", CIDIP: "idDiplomatic",
        CC: "citizenshipCard", CE: "foreignerCard",
        PATE: "drivingLicense", PATEN: "drivingLicense",
        PATNA: "nauticalLicense",
        PEP: "specialResidencePermit", DE: "foreignerDocument",
        DEXT: "foreignDocument", CD: "diplomaticCard",
        RIFUG: "refugeeTravelDocument", OTRO: "otherDocument",
      };
      const key = map[t];
      return key ? this.$t(`checkinList.group.docTypes.${key}`) : (type || "—");
    },

    minorInfo(item) {
      return item?.cliente?.minor_info || null;
    },
    hasMinorItem(item) {
      const m = this.minorInfo(item);
      return !!(m && (m.minor_document_photo_url || m.minor_authorization_photo_url));
    },

    // ── Nombre legible de la nacionalidad ────────────────────────────────────
    nationalityLabel(item) {
      const c = item?.cliente || {};

      if (this.isItaly) {
        const iso3 = String(c.pais_code || "").trim().toUpperCase();
        if (iso3) {
          const entry = ALLOGGIATI_STATI.find((s) => s.iso3 === iso3 || s.code === iso3);
          if (entry) return entry.es || entry.nome || iso3;
        }
        return iso3 || "—";
      }

      const name = String(c.nacionalidad || c.pais_name_es || "").trim();
      if (name) return name;
      return c.pais_code || "—";
    },

    // ── País de nacimiento Italia ─────────────────────────────────────────────
    paisNascitaLabel(item) {
      const c = item?.cliente || {};
      const code = c.paese_nascita_code;
      if (!code) {
        const iso3 = String(c.pais_code || "").trim().toUpperCase();
        if (iso3) {
          const entry = ALLOGGIATI_STATI.find((s) => s.iso3 === iso3);
          return entry ? (entry.es || entry.nome) : iso3 || "—";
        }
        return "—";
      }
      const entry = ALLOGGIATI_STATI.find((s) => s.code === code);
      return entry ? (entry.es || entry.nome) : code;
    },

    // ── Lugar expedición ─────────────────────────────────────────────────────
    luogoRilascioLabel(item) {
      const raw = item?.cliente?.luogo_rilascio_doc;
      if (!raw) return null;
      const iso3 = String(raw).trim().toUpperCase();
      const entry = ALLOGGIATI_STATI.find((s) => s.iso3 === iso3 || s.code === raw);
      return entry ? (entry.es || entry.nome) : raw;
    },

  },
};
</script>

<template>
  <div class="wk-section">
    <div class="wk-section__head">
      <div class="wk-section__title">{{ $t('checkinList.group.sectionTitle') }}</div>

      <button
        v-if="reservaDeLaPlanilla"
        type="button"
        class="wk-planilla"
        :disabled="bajandoPlanilla"
        :title="$t('checkinList.group.registrationFormHint')"
        @click="abrirPlanilla"
      >
        <i :class="bajandoPlanilla ? 'fa-solid fa-circle-notch fa-spin' : 'fa-regular fa-file-lines'"></i>
        {{ $t('checkinList.group.registrationForm') }}
      </button>
    </div>

    <div v-if="!associates || associates.length === 0" class="wk-empty">
      {{ $t('checkinList.group.emptyState') }}
    </div>

    <div v-else class="wk-list">
      <div
        v-for="(item, index) in associates"
        :key="item.id || index"
        class="wk-item"
        :class="{ 'wk-item--open': openIndex === index }"
      >
        <!-- ─── HEADER DE FILA ──────────────────────────────────────────────── -->
        <button type="button" class="wk-item__top" @click="toggleDetails(index)">
          <div class="wk-col wk-col--name">
            <div class="wk-label">{{ $t('checkinList.group.name') }}</div>
            <div class="wk-value wk-value--strong">{{ item.nombre }} {{ item.apellido }}</div>
          </div>
          <div class="wk-col wk-col--phone" v-if="!$isMobile">
            <div class="wk-label">{{ $t('checkinList.group.phone') }}</div>
            <div class="wk-value">{{ item?.cliente?.whatsapp || $t('checkinList.group.notAvailable') }}</div>
          </div>
          <div class="wk-col wk-col--doc" v-if="!$isMobile">
            <div class="wk-label">{{ $t('checkinList.group.document') }}</div>
            <div class="wk-value">{{ item.nu_documento || "—" }}</div>
          </div>
          <div class="wk-col wk-col--actions">
            <span
              class="wk-dot"
              :style="{ background: getMigrationColor(item.sincronizado_migracion) }"
              :title="
                item.sincronizado_migracion == 2 ? $t('checkinList.group.syncSent') :
                item.sincronizado_migracion == 3 ? $t('checkinList.group.syncError') :
                $t('checkinList.group.syncPending')
              "
            ></span>
            <span class="wk-chev">
              <i :class="openIndex === index ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'"></i>
            </span>
          </div>
        </button>

        <!-- ─── DETALLES ────────────────────────────────────────────────────── -->
        <div v-if="openIndex === index" class="wk-details">

          <!-- Bloque 1: datos directos -->
          <div class="wk-block">
            <div class="wk-block__title">{{ $t('checkinList.group.moreInfo') }}</div>
            <div class="wk-kv">
              <div class="wk-kv__row">
                <div class="wk-kv__k">{{ $t('checkinList.group.email') }}</div>
                <div class="wk-kv__v">{{ item?.cliente?.email || $t('checkinList.group.notAvailable') }}</div>
              </div>
              <div class="wk-kv__row">
                <div class="wk-kv__k">{{ $t('checkinList.group.checkIn') }}</div>
                <div class="wk-kv__v">{{ formatDateUTC(item?.cliente?.fecha_desde_hospedaje) }}</div>
              </div>
              <div class="wk-kv__row">
                <div class="wk-kv__k">{{ $t('checkinList.group.checkOut') }}</div>
                <div class="wk-kv__v">{{ formatDateUTC(item?.cliente?.fecha_hospedaje) }}</div>
              </div>
              <div class="wk-kv__row">
                <div class="wk-kv__k">{{ $t('checkinList.group.docType') }}</div>
                <div class="wk-kv__v">{{ docTypeLabel(item?.type_documento || item?.cliente?.type_documento) }}</div>
              </div>
              <!-- Soporte: solo España -->
              <div class="wk-kv__row" v-if="isVisibleData">
                <div class="wk-kv__k">{{ $t('checkinList.group.supportNumber') }}</div>
                <div class="wk-kv__v">{{ validateField(item?.cliente?.document_support) }}</div>
              </div>
            </div>

            <!-- Fotos: solo Colombia (ni España ni Italia) -->
            <div class="wk-media-stack" v-if="(item?.identity_photos || minorInfo(item)) && !isItaly && !isVisibleData">
              <div class="wk-media-section" v-if="item?.identity_photos">
                <div class="wk-media-head">
                  <div class="wk-media-title">
                    {{ $t('checkinList.group.identityVerification') }}
                    <span class="wk-media-meta" v-if="item.identity_photos.similarity != null">
                      ({{ Number(item.identity_photos.similarity).toFixed(2) }}%)
                    </span>
                    <!-- Qué pasó de verdad: con la verificación configurable, «sin fotos»
                         ya no quiere decir que algo fallara. Puede ser que el anfitrión no
                         pidiera comparación, o que el país no permita guardarlas. -->
                    <span class="wk-media-meta" v-if="item.identity_photos.status === 'no_match'">
                      · {{ $t('checkinList.group.identityNoMatch') }}
                    </span>
                    <span class="wk-media-meta" v-else-if="item.identity_photos.status === 'stored'">
                      · {{ $t('checkinList.group.identityStored') }}
                    </span>
                    <span class="wk-media-meta" v-else-if="item.identity_photos.status === 'error'">
                      · {{ $t('checkinList.group.identityError') }}
                    </span>
                  </div>
                </div>
                <div class="wk-media-list">
                  <div class="wk-media-empty" v-if="!item.identity_photos.dni_front_url && !item.identity_photos.dni_back_url && !item.identity_photos.selfie_url">
                    {{ $t('checkinList.group.identityNoPhotos') }}
                  </div>
                  <div class="wk-media-item" v-if="item.identity_photos.dni_front_url">
                    <button type="button" class="wk-media-main" @click="openPreview(item.identity_photos.dni_front_url, $t('checkinList.group.document'))">
                      <img class="wk-media-thumb" :src="item.identity_photos.dni_front_url" :alt="$t('checkinList.group.document')" />
                      <div class="wk-media-body"><div class="wk-media-name">{{ $t('checkinList.group.document') }}</div><div class="wk-media-sub">{{ $t('checkinList.group.tapToEnlarge') }}</div></div>
                    </button>
                    <div class="wk-media-actions">
                      <button type="button" class="wk-icon-btn" @click="openPreview(item.identity_photos.dni_front_url, $t('checkinList.group.document'))" :title="$t('checkinList.group.view')"><i class="fa-regular fa-eye"></i></button>
                      <a class="wk-icon-btn" :href="item.identity_photos.dni_front_download_url" target="_blank" rel="noopener" :title="$t('checkinList.group.download')"><i class="fa-solid fa-download"></i></a>
                    </div>
                  </div>
                  <div class="wk-media-item" v-if="item.identity_photos.dni_back_url">
                    <button type="button" class="wk-media-main" @click="openPreview(item.identity_photos.dni_back_url, $t('checkinList.group.documentBack'))">
                      <img class="wk-media-thumb" :src="item.identity_photos.dni_back_url" :alt="$t('checkinList.group.documentBack')" />
                      <div class="wk-media-body"><div class="wk-media-name">{{ $t('checkinList.group.documentBack') }}</div><div class="wk-media-sub">{{ $t('checkinList.group.tapToEnlarge') }}</div></div>
                    </button>
                    <div class="wk-media-actions">
                      <button type="button" class="wk-icon-btn" @click="openPreview(item.identity_photos.dni_back_url, $t('checkinList.group.documentBack'))" :title="$t('checkinList.group.view')"><i class="fa-regular fa-eye"></i></button>
                      <a class="wk-icon-btn" :href="item.identity_photos.dni_back_download_url" target="_blank" rel="noopener" :title="$t('checkinList.group.download')"><i class="fa-solid fa-download"></i></a>
                    </div>
                  </div>
                  <div class="wk-media-item" v-if="item.identity_photos.selfie_url">
                    <button type="button" class="wk-media-main" @click="openPreview(item.identity_photos.selfie_url, $t('checkinList.group.selfie'))">
                      <img class="wk-media-thumb" :src="item.identity_photos.selfie_url" :alt="$t('checkinList.group.selfie')" />
                      <div class="wk-media-body"><div class="wk-media-name">{{ $t('checkinList.group.selfie') }}</div><div class="wk-media-sub">{{ $t('checkinList.group.tapToEnlarge') }}</div></div>
                    </button>
                    <div class="wk-media-actions">
                      <button type="button" class="wk-icon-btn" @click="openPreview(item.identity_photos.selfie_url, $t('checkinList.group.selfie'))" :title="$t('checkinList.group.view')"><i class="fa-regular fa-eye"></i></button>
                      <a class="wk-icon-btn" :href="item.identity_photos.selfie_download_url" target="_blank" rel="noopener" :title="$t('checkinList.group.download')"><i class="fa-solid fa-download"></i></a>
                    </div>
                  </div>
                </div>
              </div>
              <div class="wk-media-section" v-if="minorInfo(item)">
                <div class="wk-media-head wk-media-head--split">
                  <div class="wk-media-title">{{ $t('checkinList.group.minorData') }}</div>
                  <div class="wk-media-right">
                    <span class="wk-media-k">{{ $t('checkinList.group.relationship') }}</span>
                    <span class="wk-media-v">{{ item?.cliente?.relationship || "—" }}</span>
                  </div>
                </div>
                <div class="wk-media-list">
                  <div v-if="!hasMinorItem(item)" class="wk-media-empty">{{ $t('checkinList.group.noPhotos') }}</div>
                  <div class="wk-media-item" v-if="minorInfo(item)?.minor_document_photo_url">
                    <button type="button" class="wk-media-main" @click="openPreview(minorInfo(item).minor_document_photo_url, $t('checkinList.group.minorDocument'))">
                      <img class="wk-media-thumb" :src="minorInfo(item).minor_document_photo_url" :alt="$t('checkinList.group.minorDocument')" />
                      <div class="wk-media-body"><div class="wk-media-name">{{ $t('checkinList.group.document') }}</div><div class="wk-media-sub">{{ $t('checkinList.group.minorDocument') }}</div></div>
                    </button>
                    <div class="wk-media-actions">
                      <button type="button" class="wk-icon-btn" @click="openPreview(minorInfo(item).minor_document_photo_url, $t('checkinList.group.minorDocument'))" :title="$t('checkinList.group.view')"><i class="fa-regular fa-eye"></i></button>
                      <a class="wk-icon-btn" :href="minorInfo(item).minor_document_photo_url" target="_blank" rel="noopener" :title="$t('checkinList.group.download')"><i class="fa-solid fa-download"></i></a>
                    </div>
                  </div>
                  <div class="wk-media-item" v-if="minorInfo(item)?.minor_authorization_photo_url">
                    <button type="button" class="wk-media-main" @click="openPreview(minorInfo(item).minor_authorization_photo_url, $t('checkinList.group.minorAuthorization'))">
                      <img class="wk-media-thumb" :src="minorInfo(item).minor_authorization_photo_url" :alt="$t('checkinList.group.minorAuthorization')" />
                      <div class="wk-media-body"><div class="wk-media-name">{{ $t('checkinList.group.authorization') }}</div><div class="wk-media-sub">{{ $t('checkinList.group.minorAuthorization') }}</div></div>
                    </button>
                    <div class="wk-media-actions">
                      <button type="button" class="wk-icon-btn" @click="openPreview(minorInfo(item).minor_authorization_photo_url, $t('checkinList.group.minorAuthorization'))" :title="$t('checkinList.group.view')"><i class="fa-regular fa-eye"></i></button>
                      <a class="wk-icon-btn" :href="minorInfo(item).minor_authorization_photo_url" target="_blank" rel="noopener" :title="$t('checkinList.group.download')"><i class="fa-solid fa-download"></i></a>
                    </div>
                  </div>
                </div>
                <div class="wk-media-note" v-if="minorInfo(item)?.minor_relationship_other">
                  <span class="wk-media-k">{{ $t('checkinList.group.specification') }}</span>
                  <span class="wk-media-v">{{ minorInfo(item).minor_relationship_other }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Bloque 2: información adicional -->
          <div class="wk-block">
            <div class="wk-block__title">{{ $t('checkinList.group.additionalInfo') }}</div>

            <!-- Datos comunes -->
            <div class="wk-kv">
              <div class="wk-kv__row">
                <div class="wk-kv__k">{{ $t('checkinList.group.nationality') }}</div>
                <div class="wk-kv__v">{{ nationalityLabel(item) }}</div>
              </div>
              <div class="wk-kv__row">
                <div class="wk-kv__k">{{ $t('checkinList.group.birthDate') }}</div>
                <div class="wk-kv__v">
                  {{ formatDateUTC(item?.cliente?.date_of_birth) }}
                  <span class="wk-muted">{{ $t('checkinList.group.ageYears', { age: calcAgeFromUTC(item?.cliente?.date_of_birth) }) }}</span>
                </div>
              </div>
              <div class="wk-kv__row">
                <div class="wk-kv__k">{{ $t('checkinList.group.gender') }}</div>
                <div class="wk-kv__v">
                  <template v-if="item?.cliente?.sex === 'F'">{{ $t('checkinList.group.female') }}</template>
                  <template v-else-if="item?.cliente?.sex === 'M'">{{ $t('checkinList.group.male') }}</template>
                  <template v-else>{{ item?.cliente?.sex || "—" }}</template>
                </div>
              </div>
            </div>

            <!-- ══ ESPAÑA: SES / Ertzaintza / Mossos ══ -->
            <div class="wk-subcard wk-subcard--es" v-if="isVisibleData">
              <div class="wk-subcard__tag">{{ authority.flag }} {{ authority.label }}</div>
              <div class="wk-kv">
                <div class="wk-kv__row">
                  <div class="wk-kv__k">{{ $t('checkinList.group.countryResidence') }}</div>
                  <div class="wk-kv__v">{{ validateField(item?.cliente?.country_residence) }}</div>
                </div>
                <div class="wk-kv__row">
                  <div class="wk-kv__k">{{ $t('checkinList.group.cityResidence') }}</div>
                  <div class="wk-kv__v">{{ validateField(item?.cliente?.city_residence) }}</div>
                </div>
                <div class="wk-kv__row">
                  <div class="wk-kv__k">{{ $t('checkinList.group.address') }}</div>
                  <div class="wk-kv__v">{{ validateField(item?.cliente?.address) }}</div>
                </div>
                <div class="wk-kv__row">
                  <div class="wk-kv__k">{{ $t('checkinList.group.zipCode') }}</div>
                  <div class="wk-kv__v">{{ validateField(item?.cliente?.zip_code) }}</div>
                </div>
                <div class="wk-kv__row" v-if="authority.key === 'ses_hospedaje'">
                  <div class="wk-kv__k">{{ $t('checkinList.group.sesBatchId') }}</div>
                  <div class="wk-kv__v">{{ validateField(item?.id_host) }}</div>
                </div>
              </div>
            </div>

            <!-- ══ ITALIA: Alloggiati Web ══ -->
            <div class="wk-subcard wk-subcard--italy" v-if="isItaly">
              <div class="wk-subcard__tag wk-subcard__tag--italy">🇮🇹 {{ $t('checkinList.group.alloggiatiWeb') }}</div>
              <div class="wk-kv">
                <div class="wk-kv__row">
                  <div class="wk-kv__k">{{ $t('checkinList.group.countryBirth') }}</div>
                  <div class="wk-kv__v">{{ paisNascitaLabel(item) }}</div>
                </div>
                <div class="wk-kv__row" v-if="luogoRilascioLabel(item)">
                  <div class="wk-kv__k">{{ $t('checkinList.group.docIssuePlace') }}</div>
                  <div class="wk-kv__v">{{ luogoRilascioLabel(item) }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- ─── BOTONES ──────────────────────────────────────────────────── -->
          <div class="wk-actions">
            <a class="wk-btn wk-btn--ghost" target="_blank" :href="$base.server + item.firma">
              <i class="fa-regular fa-file-lines"></i>
              {{ $t('checkinList.group.signedDocument') }}
            </a>
            <button type="button" class="wk-btn wk-btn--soft" @click="openEditDrawer(item)">
              <i class="fa-regular fa-pen-to-square"></i>
              {{ $t('checkinList.group.edit') }}
            </button>
            <button type="button" class="wk-btn wk-btn--danger" @click="emitRemove(item)">
              <i class="fa-solid fa-trash"></i>
              {{ $t('checkinList.group.remove') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ─── PREVIEW ────────────────────────────────────────────────────────── -->
    <div v-if="previewOpen" class="wk-preview" @click.self="closePreview">
      <div class="wk-preview__card">
        <div class="wk-preview__head">
          <div class="wk-preview__title">{{ previewTitle }}</div>
          <button type="button" class="wk-preview__close" @click="closePreview">
            <i class="fa-regular fa-times"></i>
          </button>
        </div>
        <div class="wk-preview__body">
          <img :src="previewUrl" :alt="$t('checkinList.group.previewAlt')" />
        </div>
      </div>
    </div>
  </div>
</template>

<style>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&family=Inter:wght@400;500;600&display=swap');
</style>

<style lang="scss" scoped>
$font-main:  'DM Sans', -apple-system, sans-serif;
$font-meta:  'Inter', -apple-system, sans-serif;
$blue:   #2563eb;
$border: #e5e7eb;
$text:   #111827;
$muted:  #6b7280;
$radius: 14px;
$radius-s: 10px;

// ── Layout ──────────────────────────────────────────────────────────────────
.wk-section        { font-family: $font-main; margin-top: 10px; }
.wk-section__title { font-family: $font-meta; font-size: 13px; font-weight: 700; color: $muted; text-transform: uppercase; letter-spacing: .5px; }

// El título a la izquierda y la planilla a la derecha: es una acción del GRUPO, no de
// un check-in, y ponerla aquí arriba es lo que la distingue del documento individual
// que tiene cada huésped en su ficha.
.wk-section__head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; margin-bottom: 10px; flex-wrap: wrap;
}

.wk-planilla {
  display: inline-flex; align-items: center; gap: 7px;
  border: 0; border-radius: 10px;
  background: rgba(103, 61, 230, .08); color: #673de6;
  font-family: $font-meta; font-size: 12.5px; font-weight: 700;
  padding: 8px 13px; cursor: pointer;
  transition: background .15s;

  i { font-size: 12px; }

  &:hover:not(:disabled) { background: rgba(103, 61, 230, .14); }
  &:disabled { opacity: .6; cursor: default; }
}
.wk-empty          { border: 1px dashed $border; background: #fafafa; border-radius: $radius; padding: 14px; font-family: $font-meta; color: $muted; font-weight: 600; font-size: 13px; }

// ── Lista de items ──────────────────────────────────────────────────────────
.wk-list  { display: flex; flex-direction: column; gap: 10px; }
.wk-item  { border: 1px solid $border; border-radius: $radius; background: #fff; overflow: hidden; transition: border-color .15s; }
.wk-item--open { border-color: #c7d2fe; }

.wk-item__top {
  width: 100%; background: #fff; border: none;
  padding: 14px; display: flex; align-items: center;
  justify-content: space-between; gap: 12px;
  cursor: pointer; text-align: left;
}

.wk-col           { display: flex; flex-direction: column; gap: 4px; }
.wk-col--name     { flex: 1.2; min-width: 0; }
.wk-col--phone    { flex: 0.9; min-width: 0; }
.wk-col--doc      { flex: 0.7; min-width: 0; }
.wk-col--actions  { display: inline-flex; align-items: center; gap: 10px; flex: 0 0 auto; }

.wk-label         { font-family: $font-meta; font-size: 11px; font-weight: 600; color: $muted; }
.wk-value         { font-size: 13px; font-weight: 600; color: $text; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wk-value--strong { font-weight: 800; }

.wk-dot  { width: 10px; height: 10px; border-radius: 999px; display: inline-block; flex-shrink: 0; }
.wk-chev { width: 32px; height: 32px; border-radius: $radius-s; border: 1px solid $border; background: #f8fafc; display: inline-flex; align-items: center; justify-content: center; color: $text; font-size: 12px; }

// ── Detalles ────────────────────────────────────────────────────────────────
.wk-details { border-top: 1px solid #eef2f7; background: #f8fafc; padding: 14px; display: flex; flex-direction: column; gap: 12px; }

.wk-block {
  background: #fff; border: 1px solid $border;
  border-radius: $radius; padding: 14px;
}
.wk-block__title { font-family: $font-meta; font-size: 12px; font-weight: 700; color: $muted; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 12px; }

// ── Key-value ───────────────────────────────────────────────────────────────
.wk-kv { display: flex; flex-direction: column; }
.wk-kv__row {
  display: flex; justify-content: space-between; align-items: baseline;
  gap: 12px; padding: 6px 0; font-size: 13px;
  & + & { border-top: 1px solid #f3f4f6; }
}
.wk-kv__k { font-family: $font-meta; color: $muted; font-weight: 600; font-size: 12px; white-space: nowrap; }
.wk-kv__v { color: $text; font-weight: 700; text-align: right; }
.wk-muted  { font-family: $font-meta; color: $muted; font-weight: 500; font-size: 12px; margin-left: 4px; }

// ── Sub-cards por país ──────────────────────────────────────────────────────
.wk-subcard {
  margin-top: 12px; border: 1px solid $border;
  border-radius: $radius-s; background: #f9fafb; padding: 12px;

  &--es    { border-color: #c7d2fe; background: #f5f7ff; }
  &--italy { border-color: #c7d2fe; background: #f5f7ff; }
}
.wk-subcard__tag {
  display: inline-flex; align-items: center; gap: 6px;
  font-family: $font-meta; font-size: 11px; font-weight: 700;
  color: #3730a3; background: #eef2ff;
  border-radius: 999px; padding: 3px 10px;
  margin-bottom: 10px;

  &--italy { color: #3730a3; background: #eef2ff; }
}

// ── Badges Alloggiati ───────────────────────────────────────────────────────
.wk-badge {
  display: inline-block; border-radius: 999px;
  padding: 2px 10px;
  font-family: $font-meta; font-size: 11px; font-weight: 700;

  &.badge--ok      { background: #dcfce7; color: #166534; }
  &.badge--error   { background: #fee2e2; color: #991b1b; }
  &.badge--pending { background: #f3f4f6; color: #374151; }
}

// ── Botones ─────────────────────────────────────────────────────────────────
.wk-actions { display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap; }
.wk-btn {
  font-family: $font-main;
  border-radius: $radius-s; padding: 9px 14px;
  font-size: 13px; font-weight: 800;
  border: 1px solid transparent;
  display: inline-flex; gap: 8px; align-items: center; cursor: pointer;
  text-decoration: none;
}
.wk-btn--ghost  { background: #fff;    border-color: $border; color: $text; }
.wk-btn--soft   { background: #eef2ff; border-color: #c7d2fe; color: #1e3a8a; }
.wk-btn--danger { background: #fff1f2; border-color: #fecaca; color: #9f1239; }

// ── Media stack ─────────────────────────────────────────────────────────────
.wk-media-stack   { display: flex; flex-direction: column; gap: 10px; margin-top: 10px; }
.wk-media-section { border: none; padding: 0; background: transparent; }
.wk-media-head    { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 8px; &--split { align-items: flex-start; } }
.wk-media-title   { font-size: 13px; font-weight: 800; color: $text; display: flex; gap: 8px; align-items: center; }
.wk-media-meta    { font-family: $font-meta; font-size: 12px; font-weight: 600; color: $muted; }
.wk-media-right   { display: inline-flex; gap: 8px; align-items: baseline; }
.wk-media-k       { font-family: $font-meta; font-size: 12px; font-weight: 600; color: $muted; }
.wk-media-v       { font-size: 12px; font-weight: 700; color: $text; }
.wk-media-list    { display: flex; flex-direction: column; gap: 8px; }
.wk-media-item    { border: 1px solid #eef2f7; background: #f8fafc; border-radius: $radius-s; padding: 8px; display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.wk-media-main    { display: flex; align-items: center; gap: 10px; background: transparent; border: none; padding: 0; cursor: pointer; text-align: left; flex: 1; }
.wk-media-thumb   { width: 56px; height: 56px; border-radius: $radius-s; object-fit: cover; border: 1px solid $border; background: #fff; flex: 0 0 auto; }
.wk-media-body    { display: flex; flex-direction: column; gap: 2px; }
.wk-media-name    { font-size: 13px; font-weight: 800; color: $text; }
.wk-media-sub     { font-family: $font-meta; font-size: 12px; font-weight: 500; color: $muted; }
.wk-media-actions { display: inline-flex; align-items: center; gap: 8px; flex: 0 0 auto; }
.wk-icon-btn      { width: 34px; height: 34px; border-radius: $radius-s; border: 1px solid $border; background: #fff; color: $text; display: inline-flex; align-items: center; justify-content: center; text-decoration: none; cursor: pointer; }
.wk-media-empty   { font-family: $font-meta; font-size: 12px; font-weight: 600; color: $muted; padding: 8px 2px; }
.wk-media-note    { margin-top: 8px; display: flex; justify-content: space-between; gap: 12px; font-family: $font-meta; font-size: 12px; font-weight: 700; background: #f8fafc; border: 1px solid #eef2f7; border-radius: $radius-s; padding: 10px; }

// ── Preview ──────────────────────────────────────────────────────────────────
.wk-preview        { position: fixed; inset: 0; background: rgba(17,24,39,.55); display: flex; align-items: center; justify-content: center; padding: 16px; z-index: 99999; }
.wk-preview__card  { width: min(820px, 100%); background: #fff; border-radius: 16px; border: 1px solid $border; overflow: hidden; }
.wk-preview__head  { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; border-bottom: 1px solid #eef2f7; }
.wk-preview__title { font-size: 14px; font-weight: 800; color: $text; }
.wk-preview__close { width: 36px; height: 36px; border-radius: $radius-s; border: 1px solid $border; background: #fff; color: $text; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.wk-preview__body  { padding: 12px; }
.wk-preview__body img { width: 100%; height: auto; border-radius: $radius-s; border: 1px solid $border; }

// ── Mobile ────────────────────────────────────────────────────────────────────
@media (max-width: 768px) {
  .wk-col--phone, .wk-col--doc { display: none; }
  .wk-col--name { min-width: 0; }
}
</style>
